import * as React from 'react';
import { cx } from '../../utils';
import { Loading } from '../../icons';
import buttonClass from './Button.styles';

/**
 * Color selection for button color prop
 */
export type ButtonColor =
  | 'accent'
  | 'caution'
  | 'chotot'
  | 'critical'
  | 'error'
  | 'info'
  | 'neutral'
  | 'positive'
  | 'primary'
  | 'success'
  | 'tertiary';

/**
 * Variant selection for button variant prop
 */
export type ButtonVariant = 'default' | 'outline' | 'solid' | 'tonal';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Different looks of button
   * @default solid
   */
  variant?: ButtonVariant;
  /**
   * Color of the background (solid) or stroke (outline) of the button
   * @default primary
   */
  color?: ButtonColor;
  /**
   * Border radius
   * @default normal
   */
  radius?: 'circle' | 'normal' | 'pill';
  /**
   * Font weight of label
   * @default bold
   */
  weight?: 'bold' | 'normal';
  /**
   * Button's size
   * @default medium
   */
  size?: 'large' | 'medium' | 'small';
  /**
   * Whether this button is disabled. No events triggered.
   * @default false
   */
  disabled?: boolean;
  /**
   * This button has equal and appropriate paddings to cater for icon buttons
   */
  icon?: boolean;
  /**
   * Whether this button stretch to fill up container's width
   */
  stretch?: boolean;
  /**
   * Element or component to be rendered as
   * @default button
   */
  as?: React.ElementType;
  /**
   * Children
   */
  children?: React.ReactNode;
  /**
   * Click event
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  /**
   * Additional classes to be added for customization
   */
  extraClasses?: string;
  /**
   * Display loading icon
   */
  isLoading?: boolean;
}

const variantResolver = (variant: string, color: string) => {
  if (variant === 'default') {
    // DEFAULT
    return `default d-${color}`;
  } else if (variant === 'outline') {
    // OUTLINE
    return `outline o-${color}`;
  } else if (variant === 'tonal') {
    // TONAL
    return `tonal t-${color}`;
  } else {
    // SOLID
    return color;
  }
};

/**
 * Renders a standard button element.
 *
 * `✅ Stable`
 */
const Button = React.forwardRef(
  (
    {
      as = 'button',
      children,
      color = 'primary',
      disabled = false,
      extraClasses = '',
      icon = false,
      onClick,
      radius = 'normal',
      size = 'medium',
      stretch = false,
      variant = 'solid',
      weight = 'bold',
      isLoading = false,
      ...props
    }: ButtonProps,
    ref
  ) => {
    let iconClass = '';
    if (icon) {
      // square/circle icon buttons
      iconClass = 'icon';
    } else if (Array.isArray(children)) {
      // if icon is used with text for label
      // detect icon position and set its margin properly
      iconClass = children?.[1]?.type?.displayName?.toLowerCase().includes('svg')
        ? 'i-right'
        : 'i-left';
    } else if (isLoading && children) {
      // if loading icon is used with label
      iconClass = 'i-left';
    }

    // prettier-ignore
    const className = cx(
      buttonClass,
      variantResolver(variant, color),
      `r-${radius}`,
      size,
      `w-${weight}`,
      iconClass,
      stretch && 'stretch',
      disabled && 'disabled',
      isLoading && 'loading',
      extraClasses
    );

    const attrs = {
      ref,
      className,
      disabled: disabled && true,
      onClick: onClick && !disabled && onClick,
      ...props,
    };

    return React.createElement(
      as,
      attrs,
      isLoading && React.createElement(Loading, { className: 'loading-icon' }),
      children
    );
  }
);

export default Button;
